import { AutoTourEntry } from "@/controllers/tutorials";

/**
 * Match the current location against the auto-show map.
 *
 * A pattern is a route path which may be written three ways, and a tour picks
 * the one matching how widely it should fire:
 *
 *   "/s/[exchange]/[ticker]/short-interest"  a Next.js route template — fires
 *                                            once per user across every stock
 *   "/alt-data/vehicles"                     a concrete path — one instance of
 *                                            a dynamic route
 *   "/short-interest?tab=heatmap"            a path plus a query constraint
 *
 * The last two exist because several distinct products share one pathname.
 * Short Interest has nine ?tab= products behind "/short-interest" and Alt Data
 * routes every dataset through "/alt-data/[source]", so matching the template
 * alone would hand all of them the same tour and leave the tabs a user does not
 * land on first unguided forever.
 *
 * Most specific wins: a query constraint beats a concrete path, which beats a
 * template. So "/alt-data/vehicles" takes precedence over "/alt-data/[source]",
 * and the template still catches every dataset that has no tour of its own.
 */
export const matchAutoTour = (autoTours: AutoTourEntry[], pathname: string, asPath: string): AutoTourEntry | undefined => {
    const [currentPath, currentQuery] = asPath.split("?");
    const current = new URLSearchParams(currentQuery ?? "");
    const specificity = (pattern: string) => (pattern.includes("?") ? 2 : 0) + (pattern.includes("[") ? 0 : 1);
    const candidates = autoTours
        .flatMap((entry) =>
            entry.route
                .split(/\s+/)
                .filter(Boolean)
                .map((pattern) => ({ entry, pattern })),
        )
        // Stable sort — ties keep the server's ordering rather than reshuffling.
        .sort((a, b) => specificity(b.pattern) - specificity(a.pattern));

    const hit = candidates.find(({ pattern }) => {
        const [patternPath, patternQuery] = pattern.split("?");
        // Either the route template (fires for every instance) or the concrete
        // URL (fires for exactly one).
        if (patternPath !== pathname && patternPath !== currentPath) return false;
        if (!patternQuery) return true;
        // forEach rather than iterating the URLSearchParams: the project's TS
        // target does not allow spreading an iterator.
        let matched = true;
        new URLSearchParams(patternQuery).forEach((value, key) => {
            if (current.get(key) !== value) matched = false;
        });
        return matched;
    });
    return hit?.entry;
};
